import { BaseScreen, Device, i18n, waitFor } from '@goodboydigital/astro';
import { euclideanModulo, lerp } from '@goodboydigital/odie';
import { Container } from '@pixi/display';
import { Point, Sprite, Texture } from 'pixi.js';

import { App, Shared } from '../App';
import { AnalyticsEvent, AnalyticsState } from '../plugins/AnalyticsPlugin';
import { NineSliceButton } from '../ui/buttons/NineSliceButton';
import { GestureArea } from '../ui/GestureArea';
import LocalisedText from '../ui/LocalisedText';
import { SelectPanelButton } from './../ui/buttons/SelectPanelButton';
import { CharacterCard } from './../ui/CharacterCard';
import { Panel } from './../ui/Panel';

interface CardPosData
{
    x: number,
    y: number,
    scale: number,
}

interface CardMeta
{
    id: string,
    iconId: string,
    characterSprite: Sprite,
    titleText: string,
    logoAsset: string,
    currentlySelected: boolean,
    new: boolean,
    unlockCoinCost: number
    unlockDiamondCost: number,
    unlocked: boolean,
}

const posConfig: Record<string, CardPosData> = {
    // '-5': { x: -490, y: 0, scale: 0 },
    '-4': { x: -490, y: 0, scale: 0.7 },
    '-3': { x: -490, y: 0, scale: 0.8 },
    '-2': { x: -420, y: 0, scale: 0.9 },
    '-1': { x: -320, y: 0, scale: 1 },
    0: { x: 0, y: 0, scale: 1.5 },
    1: { x: 320, y: 0, scale: 1 },
    2: { x: 420, y: 0, scale: 0.9 },
    3: { x: 490, y: 0, scale: 0.8 },
    4: { x: 490, y: 0, scale: 0.7 },
    // 5: { x: 490, y: 0, scale: 0 },
};

export class CharacterSelectScreen extends BaseScreen
{
    public app: App;
    public manifests: string[] = [];
    public bg!: Sprite;
    public area!: GestureArea;
    public title!: LocalisedText;
    public playText!: LocalisedText;
    public backButton!: NineSliceButton;
    public characterPanel!: Panel;
    public studioPanel!: Panel;
    public playButton!: NineSliceButton;

    public characterPanelButton!: SelectPanelButton;
    public rideStudioButton!: SelectPanelButton;

    public characterIcon!: Sprite;
    public vehicleIcon!: Sprite;

    public cardMeta: CardMeta[];
    public cardContainer!: Container;

    public leftBtn!: NineSliceButton;
    public rightBtn!: NineSliceButton;

    protected tweenDuration = 0.2;

    private _cards!: CharacterCard[];
    private _currentCharacterID: string;
    private _carouselPosition = 0;

    constructor(app: App)
    {
        super(app);
        this.app = app;
        this.cardMeta = [];
        this._currentCharacterID = '';
    }

    async init(): Promise<void>
    {
        if (this.bg) return;
        this.bg = Sprite.from(Texture.WHITE);
        this.bg.alpha = 0;
        this.bg.width = this.bg.height = 9999;
        this.bg.anchor.set(0.5);
        this.view.addChild(this.bg);

        this.area = new GestureArea();
        this.view.addChild(this.area);

        const backIcon = Sprite.from(Shared.assets.getTexture('icon-back-arrow'));

        this.backButton = new NineSliceButton({
            icon: backIcon,
            accessibleTitle: 'Back',
            w: backIcon.width * 1.5,
            h: backIcon.height * 1.5,
        });
        this.view.addChild(this.backButton.view);
        this.backButton.onPress.connect(() =>
        {
            this.app.controller.toGarage();
        });

        this.title = new LocalisedText('characterSelectScreen_title', {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            stroke: 0x333333,
            strokeThickness: 10,
            lineJoin: 'round',
            align: 'center',
            fontSize: 54,
            maxWidth: 650,
        });
        this.title.anchor.set(0.5);
        this.view.addChild(this.title);

        this.cardContainer = new Container();
        this.cardContainer.sortableChildren = true;
        this.view.addChild(this.cardContainer);

        this._createMetaHash();
        this._createCards();
        this._orderCards();

        const leftArrow = Sprite.from(Shared.assets.getTexture('updown-arrow'));

        leftArrow.rotation = Math.PI * 0.5;
        leftArrow.scale.x *= 1.2;
        leftArrow.scale.y *= 1.2;
        this.leftBtn = new NineSliceButton({
            icon: leftArrow,
            w: 80,
            h: 220,
        });
        this.leftBtn.view.visible = !Device.mobile;
        this.leftBtn.onPress.connect(() => this.handleLeftClick());

        this.view.addChild(this.leftBtn.view);

        const rightArrow = Sprite.from(Shared.assets.getTexture('updown-arrow'));

        rightArrow.rotation = Math.PI * -0.5;
        rightArrow.scale.x *= 1.2;
        rightArrow.scale.y *= 1.2;
        this.rightBtn = new NineSliceButton({
            icon: rightArrow,
            w: 80,
            h: 220,
        });
        this.rightBtn.view.visible = !Device.mobile;
        this.rightBtn.onPress.connect(() => this.handleRightClick());

        this.view.addChild(this.rightBtn.view);

        const playIcon = new Sprite();

        this.playText = new LocalisedText('characterSelectScreen_play', {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            stroke: 0x333333,
            strokeThickness: 10,
            lineJoin: 'round',
            align: 'center',
            fontSize: 44,
            maxWidth: 250,
        });

        this.playText.anchor.set(0.5);
        playIcon.addChild(this.playText);
        
        this.playButton = new NineSliceButton({
            icon: playIcon,
            accessibleTitle: 'GO',
            bgAsset: 'button-ninesliced-magenta',
            w: 275,
            h: 100,
        });
        this.playButton.onPress.connect(async () =>
        {
            this.app.analytics.trackEvent(AnalyticsEvent.CHARACTERSELECTPAGE_RACE_CLICK);
            this.app.controller.toPreRace();
            await waitFor(100);
            this._cards.forEach((card) => card.setFocus(false, false));
        });
        this.view.addChild(this.playButton.view);

        this._currentCharacterID = Shared.game.garage.user.data.vehicle.character;
    }

    public show(): void
    {
        
        this.resize(this.width, this.height);
        this.view.visible = true;
        
        this.app.analytics.trackState(AnalyticsState.CHARACTER_SELECT);

        this._currentCharacterID = Shared.game.garage.user.data.vehicle.character;
        Shared.game.soundSystem.playMusic('menu-music');
        
        for (let i = 0; i < this._cards.length; i++)
        {
            const card = this._cards[i];
            
            card.show();

            if (card.id === this._currentCharacterID)
            {
                this._carouselPosition = i;
                this.area.setPosX(i);
            }
            
            this._cards[i].setFocus(false);
        }
        
        this.playButton.view.visible = false;
        this.playButton.show(true);

        this.onSnap(false);
        
        this.update();
    }

    public hide(): void
    {
        this.playButton.hide(true);
    }

    public hidden(): void
    {
        this.view.visible = false;

        Shared.game.shop.setCharacter(this._currentCharacterID);
        // Shared.game.shop.saveVehicleSelection();
    }

    public resize(w: number, h: number): void
    {
        super.resize(w, h);
        this.view.x = (this.width - this.app.width) * 0.5;
        this.view.y = (this.height - this.app.height) * 0.5;
        this.bg.position.copyFrom(this.app.stageCenter as Point);

        this.backButton.view.position.set(
            this.app.stageLeft + 90,
            this.app.stageTop + 90,
        );

        this.title.x = this.app.stageCenter.x;
        this.title.y = this.app.stageTop + (this.title.height / 2) + 20;

        this.cardContainer.position.set(
            this.app.stageCenter.x,
            this.app.stageCenter.y,
        );

        this.leftBtn.view.position.set(
            this.app.stageLeft + 75,
            this.app.stageCenter.y,
        );

        this.rightBtn.view.position.set(
            this.app.stageRight - 75,
            this.app.stageCenter.y,
        );

        this.area.width = this.app.width;
        this.area.height = this.app.height;

        this.playButton.view.position.set(
            this.app.stageRight - 170,
            this.app.stageBottom - 90);
    }

    public handleLeftClick(): void
    {
        this.area.prev();
        this._orderCards();
    }

    public handleRightClick(): void
    {
        this.area.next();
        this._orderCards();
    }

    private _createCards(): void
    {
        this._cards = [];

        const shopSystem = Shared.game.shop;

        this.cardMeta.forEach((meta) =>
        {
            console.log(meta.titleText)
            meta.characterSprite.scale.set(1.2);

            const card = new CharacterCard({
                ...meta,
                tweenDuration: this.tweenDuration,
                inFocus: false,
                unlocked: shopSystem.checkPurchased(meta.id, 'character'),
            });

            card.purchasePanel.signals.coinBtnClick.connect((cost) =>
            {
                if (shopSystem.checkCoinsAvailable(cost))
                {
                    this.app.popup.presentConfirmPurchasePopup({
                        title: 'purchasePopup_title',
                        message: 'purchasePopup_message',
                        confirm: 'purchasePopup_confirm',
                        cancel: 'purchasePopup_cancel',
                        itemName: i18n.translate(`character_${meta.titleText}`),
                        itemSprite: meta.iconId,
                        itemBgAsset: 'item-holder-large',
                        coinCost: cost,
                        onConfirm: () => this._purchaseCharacter(meta.id, card, cost),
                    });
                }
            });

            card.purchasePanel.signals.diamondBtnClick.connect((cost) =>
            {
                if (shopSystem.checkDiamondsAvailable(cost))
                {
                    this.app.popup.presentConfirmPurchasePopup({
                        title: 'purchasePopup_title',
                        message: 'purchasePopup_message',
                        confirm: 'purchasePopup_confirm',
                        cancel: 'purchasePopup_cancel',
                        itemName: meta.titleText,
                        itemSprite: meta.iconId,
                        itemBgAsset: 'item-holder-large',
                        diamondCost: cost,
                        onConfirm: () => this._purchaseCharacter(meta.id, card, 0, cost),
                    });
                }
            });

            card.selectBtn.onPress.connect(() =>
            {
                this.app.analytics.trackEvent(AnalyticsEvent.CUSTOMISEPAGE_ITEMSELECTION_CLICK, { game_item: `characters|${meta.id}` });
                this._setActiveCard(meta.id);
                this._currentCharacterID = meta.id;
                Shared.game.garage.saveVehicleSelection();
            });

            this._cards.push(card);
            this.cardContainer.addChild(card.view);
        });
    }
    
    private _purchaseCharacter(id: string, card: CharacterCard, coins?: number, diamonds?: number): void
    {
        const shopSystem = Shared.game.shop;
        shopSystem.purchaseItem(id, 'character', coins, diamonds);
        card.unlocked = true;
        this._currentCharacterID = id;
        this._setActiveCard(id);
        this.app.analytics.trackEvent(AnalyticsEvent.CONTENT_UNLOCKED, { game_item: `character|${id}`});
        Shared.game.garage.saveVehicleSelection();
        
        if(shopSystem.checkAllTypeUnlocked("character")){
            this.app.analytics.trackEvent(AnalyticsEvent.PURCHASED_ALL_ITEMS, { game_item: `character|${id}`, content_type: 'character'});
        }
    }

    private async _orderCards(): Promise<void>
    {
        const len = this._cards.length;
        const pos = Math.ceil(-this._carouselPosition) + 3;
        const ratio = euclideanModulo(this._carouselPosition, 1);

        for (let i = 0; i < len; i++)
        {
            const card = this._cards[i];
            const slotIndex = euclideanModulo((i + pos), len) - 3;
            const slot = posConfig[slotIndex];
            const nextSlot = posConfig[slotIndex - 1];

            card.view.renderable = !!slot && !!nextSlot;

            if (slot && nextSlot)
            {
                card.view.x = lerp(slot.x, nextSlot.x, ratio);
                card.view.y = lerp(slot.y, nextSlot.y, ratio);
                card.scale = lerp(slot.scale, nextSlot.scale, ratio);
                card.view.zIndex = card.scale;
            }
        }
    }

    private _createMetaHash(): void
    {
        const availableCharacters = this.app.data.getAvailableCharacters();

        for (const f of availableCharacters)
        {
            const charMeta = this.app.data.getCharacterData(f);
            const characterIcon = Sprite.from(Shared.assets.getTexture(charMeta.iconId));
            const currentCharId = Shared.game.garage.user.data.vehicle.character;

            characterIcon.anchor.set(0.5);

            this.cardMeta.push({
                id: charMeta.id,
                iconId: charMeta.iconId,
                characterSprite: characterIcon,
                titleText: charMeta.id,
                logoAsset: charMeta.logoAsset,
                new: !!charMeta.new,
                currentlySelected: charMeta.id === currentCharId,
                unlockCoinCost: charMeta.coinPrice,
                unlockDiamondCost: charMeta.diamondPrice,
                unlocked: !!charMeta.unlocked,
            });

            if (charMeta.unlocked) Shared.game.shop.purchaseItem(charMeta.id, 'character', 0);
        }
    }

    private _setActiveCard(id: string): void
    {
        this._cards.forEach((card) =>

        {
            card.currentlySelected = card.id === id;
        });
    }

    public update(): void
    {
        this.area.update();
        this._carouselPosition = euclideanModulo(this.area.posX, this._cards.length);
        if (this.area.changed) this.onChange();
        if (this.area.snapped) this.onSnap();
        this._orderCards();
    }

    public getSelectedCardIndex(): void
    {
        //
    }

    public onSnap(animate = true): void
    {
        const card = this._cards[Math.round(this._carouselPosition)];

        card.setFocus(true, animate);

        if (animate) Shared.game.soundSystem.playSfx('slide');

        Shared.game.garage.setCharacter(card.id);
    }

    public onChange(): void
    {
        this._cards.forEach((card) => card.setFocus(false, true));
    }
}
